/*
 * @Description: 实时定位驾驶舱 — 告警铃铛角标数据 hook
 *   轮询 D5 /alerts/unread-count 未读数；打开告警记录时调 D4 /alerts/read 标记全部已读（角标清零）。
 *   告警记录列表由 AlertRecordModal 自行走 D1 /alerts/list。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import {
  fetchAlertUnreadCount,
  markAlertsRead
} from '../../../actions/locationCockpit'
import { REALTIME_POLL_INTERVAL_MS, isCockpitMockEnabled } from '../constants'
import { MOCK_ALERT_UNREAD_COUNT } from '../mock/alerts.mock'
import { AlertUnreadCountData, CockpitEnvelope } from '../types/cockpit.types'

export interface UseAlertBellResult {
  /** 未读告警数（角标，>0 才展示） */
  unreadCount: number
  /** 标记全部已读（角标清零，打开告警记录时调用） */
  markAllRead: () => void
}

/**
 * 告警铃铛角标：未读数轮询 + 标记已读。
 *
 * @param groupId - 当前团队长 id
 */
export function useAlertBell(groupId: string): UseAlertBellResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [unreadCount, setUnreadCount] = useState(0)
  const aliveRef = useRef(true)

  const refreshUnread = useCallback(() => {
    if (isMock) {
      setUnreadCount(MOCK_ALERT_UNREAD_COUNT)
      return
    }
    if (!groupId) return
    dispatch(fetchAlertUnreadCount({ groupId }))
      .then((res: CockpitEnvelope<AlertUnreadCountData>) => {
        if (!aliveRef.current) return
        if (res && res.code === 200) setUnreadCount(res.data?.count ?? 0)
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
  }, [dispatch, groupId, isMock])

  useEffect(() => {
    aliveRef.current = true
    refreshUnread()
    if (isMock) {
      return () => {
        aliveRef.current = false
      }
    }
    const timer = window.setInterval(refreshUnread, REALTIME_POLL_INTERVAL_MS)
    return () => {
      aliveRef.current = false
      window.clearInterval(timer)
    }
  }, [refreshUnread, isMock])

  const markAllRead = useCallback(() => {
    // 角标先乐观清零
    setUnreadCount(0)
    if (isMock || !groupId) return
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    dispatch(markAlertsRead({ groupId })).catch(() => {})
  }, [dispatch, groupId, isMock])

  return { unreadCount, markAllRead }
}
